import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

import { useLanguage } from '../../contexts/LanguageContext';
import { deduplicateRecurring } from '../../utils/deduplicateRecurring';
import { upcomingQueryStart, isOngoingOrUpcoming, formatEventDateRange } from '../../utils/eventDates';

function UpcomingPage() {
  const { language } = useLanguage();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // Query window is widened so multi-day events already underway are still
    // fetched; isOngoingOrUpcoming filters out genuinely past events.
    const upcomingQuery = query(
      collection(db, 'events'),
      where('date', '>=', upcomingQueryStart(startOfToday)),
      orderBy('date', 'asc')
    );

    const unsubscribe = onSnapshot(upcomingQuery, (snapshot) => {
      const events = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(event => isOngoingOrUpcoming(event, startOfToday));
      setUpcomingEvents(deduplicateRecurring(events));
      setLoading(false);
    }, (error) => {
      console.warn('Error fetching events:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatDate = (event) => {
    if (event.date && event.date.toDate) {
      return formatEventDateRange(event.date, event.endDate, 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return 'Date TBD';
  };

  const formatTime = (date) => {
    if (date && date.toDate) {
      return date.toDate().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return '';
  };

  const getEventTypeLabel = (type) => {
    switch(type) {
      case 'music': return 'Music Event';
      case 'food': return 'Food Event';
      case 'workshop': return 'Workshop';
      case 'performance': return 'Performance';
      default: return 'Event';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          marginBottom: '20px', 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif' 
        }}>
          Upcoming Events
        </h1>
        <div style={{ padding: '20px', textAlign: 'center', fontSize: '1.1rem', color: '#666' }}>
          Loading events...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ 
        fontSize: '1.8rem', 
        marginBottom: '20px', 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        textTransform: 'uppercase'
      }}>
        Upcoming Events
      </h1>
      <p style={{ 
        fontSize: '0.9rem', 
        lineHeight: '1.6', 
        color: '#666',
        marginBottom: '30px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif' 
      }}>
        All upcoming events at Idea Farm, ordered by date. From music performances to food events and workshops.
      </p>

      {upcomingEvents.length === 0 ? (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          fontSize: '0.9rem', 
          color: '#666',
          backgroundColor: '#f9f9f9',
          border: '1px solid #ddd',
          borderRadius: '8px',
          textTransform: 'uppercase'
        }}>
          No upcoming events scheduled at the moment. Check back soon!
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {upcomingEvents.map(event => (
            <Link 
              key={event.id} 
              to={`/event/${event.id}`}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                padding: '20px',
                backgroundColor: '#fff',
                display: 'flex',
                gap: '20px',
                alignItems: 'flex-start',
                marginBottom: '20px',
                cursor: 'pointer'
              }}>
                {(event.thumbnailUrl || event.imageUrl) && (
                  <img 
                    src={event.thumbnailUrl || event.imageUrl} 
                    alt={(language === 'sv' && event.title_sv) || event.title}
                    style={{
                      width: '200px',
                      height: '150px',
                      objectFit: 'cover',
                      flexShrink: 0
                    }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.9rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    marginBottom: '5px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
                  }}>
                    {getEventTypeLabel(event.type)}
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    margin: '0 0 10px 0',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                    textTransform: 'uppercase'
                  }}>
                    {(language === 'sv' && event.title_sv) || event.title}
                  </h3>
                  <div style={{
                    fontSize: '1.1rem',
                    color: '#333',
                    marginBottom: '10px',
                    fontWeight: '500',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                    textTransform: 'uppercase'
                  }}>
                    {formatDate(event)} {formatTime(event.date) && `at ${formatTime(event.date)}`}
                  </div>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.5',
                    color: '#666',
                    margin: '0 0 10px 0',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                    textTransform: 'uppercase'
                  }}>
                    {(language === 'sv' && event.description_sv) || event.description}
                  </p>
                  {event.price && (
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#000',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                      textTransform: 'uppercase'
                    }}>
                      Price: {event.price} SEK
                    </div>
                  )}
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#666',
                    marginTop: '10px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
                    textTransform: 'uppercase',
                    fontWeight: 'bold'
                  }}>
                    View Details →
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default UpcomingPage;
